import React, { useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import {
  scrollToTop,
  qufree_hero1,
  qufree_hero2,
  qufree_logo,
} from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import HomePageFooter from "src/views/home/components/Footer";
import Footer from "src/components/footer";

// Map Pin Component with Circular Badge and Solid Triangular Pointer
const MapPin: React.FC<{ num: string; color: "teal" | "yellow" }> = ({
  num,
  color,
}) => {
  const strokeColor = color === "teal" ? "#0DC2B5" : "#F5CF46";
  const numColor = strokeColor;
  const displayNum = num;

  return (
    <div className={`map-pin-badge pin-${color}`}>
      <svg
        viewBox="0 0 46 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="pin-svg-icon"
      >
        {/* Solid downward triangle pointer */}
        <polygon
          points="8,28 38,28 23,56"
          fill={strokeColor}
          stroke={strokeColor}
          strokeWidth="1"
          strokeLinejoin="round"
        />
        {/* Circle with white fill and thinner colored stroke */}
        <circle
          cx="23"
          cy="21"
          r="18.5"
          fill="#FFFFFF"
          stroke={strokeColor}
          strokeWidth="1.8"
        />
        {/* Centered Number with decreased font weight */}
        <text
          x="23"
          y="22"
          textAnchor="middle"
          dominantBaseline="central"
          fill={numColor}
          fontSize="22"
          fontWeight="500"
          fontFamily="'Open Sans', -apple-system, BlinkMacSystemFont, sans-serif"
        >
          {displayNum}
        </text>
      </svg>
    </div>
  );
};

// Connecting Wave SVG (alternating bottom troughs and top crests between 5 cards)
const ConnectingWave: React.FC = () => {
  return (
    <svg
      className="flow-connecting-wave"
      viewBox="0 0 1000 440"
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Wave 1: Under Card 1 and Card 2 (exact same visible amplitude as top wave) */}
      <path
        d="M 130 320 C 145 423, 255 423, 270 320"
        stroke="#0DC2B5"
        strokeWidth="1.8"
        strokeDasharray="5 5"
        vectorEffect="non-scaling-stroke"
      />
      {/* Wave 2: Over between Pin 2 and Pin 3 (exact same visible amplitude) */}
      <path
        d="M 330 115 C 345 15, 455 15, 470 115"
        stroke="#0DC2B5"
        strokeWidth="1.8"
        strokeDasharray="5 5"
        vectorEffect="non-scaling-stroke"
      />
      {/* Wave 3: Under Card 3 and Card 4 (exact same visible amplitude as top wave) */}
      <path
        d="M 530 320 C 545 423, 655 423, 670 320"
        stroke="#0DC2B5"
        strokeWidth="1.8"
        strokeDasharray="5 5"
        vectorEffect="non-scaling-stroke"
      />
      {/* Wave 4: Over between Pin 4 and Pin 5 (exact same visible amplitude) */}
      <path
        d="M 730 115 C 745 15, 855 15, 870 115"
        stroke="#0DC2B5"
        strokeWidth="1.8"
        strokeDasharray="5 5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const Qufree: React.FC = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  const featureCards = [
    {
      num: "1.",
      title: "Order Ahead, Skip the Line",
      desc: "Customers place orders before they arrive, so pickup is instant.",
    },
    {
      num: "2.",
      title: "Find What's Nearby",
      desc: "Location-based discovery surfaces the closest, most relevant vendors.",
    },
    {
      num: "3.",
      title: "OTP-Verified Handoffs",
      desc: "Every pickup is confirmed with a one-time code — no mix-ups, no disputes.",
    },
    {
      num: "4.",
      title: "Pay However You Like",
      desc: "Pay at pickup — no card details or online payment required upfront.",
    },
    {
      num: "5.",
      title: "Live Order Status",
      desc: "Both sides see real-time updates, from order placed to on the way.",
    },
    {
      num: "6.",
      title: "Built for Local Vendors",
      desc: "Lightweight tools that fit into how small shops already operate.",
    },
  ];

  const customerSteps = [
    {
      num: "1",
      color: "teal" as const,
      stepTag: "STEP 1",
      title: "Discover Nearby",
      desc: "Find shops and products available near your location.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      ),
    },
    {
      num: "2",
      color: "yellow" as const,
      stepTag: "STEP 2",
      title: "Reorder Favorites",
      desc: "Quickly reorder your favorite items from history.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      ),
    },
    {
      num: "3",
      color: "teal" as const,
      stepTag: "STEP 3",
      title: "Pay When You Arrive",
      desc: "Skip online payments and settle your bill at the shop.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      ),
    },
    {
      num: "4",
      color: "yellow" as const,
      stepTag: "STEP 4",
      title: "Real-Time Updates",
      desc: "Get notified instantly when your order is ready.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
    },
    {
      num: "5",
      color: "teal" as const,
      stepTag: "STEP 5",
      title: "Secure OTP Login",
      desc: "Log in securely using OTP, with no passwords.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
  ];

  const vendorSteps = [
    {
      num: "1",
      color: "teal" as const,
      stepTag: "STEP 1",
      title: "Manage Products",
      desc: "List and update products in a few taps.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      ),
    },
    {
      num: "2",
      color: "yellow" as const,
      stepTag: "STEP 2",
      title: "Live Orders",
      desc: "See every incoming order the moment it lands.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
    },
    {
      num: "3",
      color: "teal" as const,
      stepTag: "STEP 3",
      title: "Order Actions",
      desc: "Accept, prepare, or decline orders on the go.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      ),
    },
    {
      num: "4",
      color: "yellow" as const,
      stepTag: "STEP 4",
      title: "Stock Control",
      desc: "Keep stock and availability accurate in real time.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 21h18" />
          <path d="M5 21V7l7-4 7 4v14" />
          <path d="M9 11h6" />
          <path d="M9 15h6" />
        </svg>
      ),
    },
    {
      num: "5",
      color: "teal" as const,
      stepTag: "STEP 5",
      title: "OTP Verification",
      desc: "Confirm every handoff with OTP-based verification.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      ),
    },
  ];

  const radialSteps = [
    {
      id: "discover",
      title: "Discover",
      desc: "Find shops and vendors near you, based on your live location.",
      positionClass: "pos-top",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
        </svg>
      ),
    },
    {
      id: "prepare",
      title: "Prepare",
      desc: "The vendor gets notified instantly and starts preparing your order.",
      positionClass: "pos-top-right",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
        </svg>
      ),
    },
    {
      id: "arrive",
      title: "Arrive",
      desc: "Walk in whenever it suits you - no line, no waiting.",
      positionClass: "pos-bottom-right",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      id: "verify",
      title: "Verify & Collect",
      desc: "Confirm the handoff with a one-time OTP and you're done.",
      positionClass: "pos-bottom",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      ),
    },
    {
      id: "track",
      title: "Track",
      desc: "Get real-time updates as your order moves toward ready.",
      positionClass: "pos-bottom-left",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 14 14" />
        </svg>
      ),
    },
    {
      id: "order",
      title: "Order",
      desc: "Pick your products and place the order in a few taps.",
      positionClass: "pos-top-left",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>
      ),
    },
  ];

  return (
    <div className="qufree-container animate__animated animate__fadeIn">
      {/* 1. Hero Section */}
      <section className="qufree-hero-section">
        <div className="hero-backdrop-pillar" />
        <div className="qufree-hero-content">
          <div className="hero-left-block animate__animated animate__fadeInLeft">
            <div className="hero-text-content">
              <h1 className="qufree-hero-title">QuFree</h1>
              <p className="qufree-hero-desc">
                QuFree is our all-in-one local commerce platform that connects customers
                with nearby shops for fast, queue-free ordering and pickup. Customers order
                ahead, vendors prepare with ease, and pickup happens in minutes — not in a line.
              </p>
            </div>
          </div>
          <div className="hero-right-logo animate__animated animate__fadeInRight">
            <img src={qufree_logo} alt="QuFree Logo" className="qufree-logo-img" />
          </div>
        </div>
        <div className="qufree-breadcrumb">
          <Link to="/" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={pageRoutes.Marketplace} className="breadcrumb-link">
            Marketplace
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">QuFree</span>
        </div>
      </section>

      {/* 2. "What is QuFree?" Section */}
      <section className="what-is-qufree-section">
        <div className="what-is-qufree-grid">
          <div className="what-is-text-col animate__animated animate__fadeInUp">
            <div className="what-is-header-row">
              <h2 className="what-is-outline-title">
                What is
                <br />
                <span className="qufree-outline-row">
                  QuFree?
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>

            <p className="what-is-p1">
              We built{" "}
              <a
                href="https://qufree.wingmp.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="highlight-teal-link"
              >
                QuFree
              </a>{" "}
              to solve
              a problem every neighborhood shop and customer knows well — long
              queues, slow checkouts, and no way to order ahead. QuFree gives customers
              a simple way to discover nearby vendors, place an order in advance,
              and walk in only to collect it. On the other side, vendors get a
              lightweight app to receive, manage, and prepare orders without disrupting
              how their shop already runs.
            </p>

            <p className="what-is-platform-line">
              The platform is built around two purpose-made apps:
            </p>

            <div className="what-is-mini-cards">
              <div className="mini-card">
                <h4 className="mini-card-title">Customer App</h4>
                <p className="mini-card-desc">
                  Discover nearby shops, order ahead, and skip the queue entirely.
                </p>
              </div>
              <div className="mini-card mini-card-vendor">
                <h4 className="mini-card-title">Vendor App</h4>
                <p className="mini-card-desc">
                  Receive orders, manage stock, and hand off pickups without any added friction.
                </p>
              </div>
            </div>
          </div>

          <div className="what-is-mockup-col animate__animated animate__zoomIn">
            <div className="qufree-layered-mockup">
              <img
                src={qufree_hero1}
                alt="QuFree Shop Screen"
                className="qufree-mockup-bg"
              />
              <img
                src={qufree_hero2}
                alt="QuFree Splash Screen"
                className="qufree-mockup-fg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. "Designed Around Speed, Trust, and Simplicity" Section */}
      <section className="qufree-features-section">
        <div className="section-header">
          <h2 className="section-title">
            Designed Around <span className="highlight-teal">Speed</span>,{" "}
            <span className="highlight-teal">Trust</span>, and{" "}
            <span className="highlight-teal">Simplicity</span>
          </h2>
        </div>

        <div className="features-grid">
          {featureCards.map((card, idx) => (
            <div className="feature-card" key={idx}>
              <div className="feature-num">{card.num}</div>
              <h3 className="feature-card-title">{card.title}</h3>
              <p className="feature-card-desc">{card.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4. "One Experience for Customers, One for Vendors" Section */}
      <section className="qufree-dual-experience-section">
        <div className="section-header">
          <h2 className="section-title">
            One Experience for <span className="highlight-teal">Customers</span>,
            One for <span className="highlight-teal">Vendors</span>
          </h2>
        </div>

        {/* Customer Flow */}
        <div className="flow-container customer-flow">
          <div className="flow-badge-wrap">
            <div className="flow-badge badge-customers">FOR CUSTOMERS</div>
          </div>

          <div className="flow-stepper-track">
            <ConnectingWave />
            {customerSteps.map((step, idx) => (
              <div className="stepper-node" key={idx}>
                <MapPin num={step.num} color={step.color} />
                <div className={`step-card border-${step.color}`}>
                  <div className="step-card-header">
                    <div className={`step-icon-wrap icon-${step.color}`}>
                      {step.icon}
                    </div>
                    <span className={`step-card-tag tag-${step.color}`}>
                      {step.stepTag}
                    </span>
                  </div>
                  <h4 className="step-card-title">{step.title}</h4>
                  <p className="step-card-desc">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vendor Flow */}
        <div className="flow-container vendor-flow">
          <div className="flow-badge-wrap align-right">
            <div className="flow-badge badge-vendors">FOR VENDORS</div>
          </div>

          <div className="flow-stepper-track">
            <ConnectingWave />
            {vendorSteps.map((step, idx) => (
              <div className="stepper-node" key={idx}>
                <MapPin num={step.num} color={step.color} />
                <div className={`step-card border-${step.color}`}>
                  <div className="step-card-header">
                    <div className={`step-icon-wrap icon-${step.color}`}>
                      {step.icon}
                    </div>
                    <span className={`step-card-tag tag-${step.color}`}>
                      {step.stepTag}
                    </span>
                  </div>
                  <h4 className="step-card-title">{step.title}</h4>
                  <p className="step-card-desc">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. "From Browsing to Pickup in Six Simple Steps" Section */}
      <section className="qufree-radial-section">
        <div className="section-header">
          <h2 className="section-title">
            From Browsing to Pickup in{" "}
            <span className="highlight-teal">Six Simple Steps</span>
          </h2>
          <p className="section-subtitle">
            QuFree simplifies local commerce on both sides. Discover, choose, and
            retrieve your orders in a process that just flows.
          </p>
        </div>

        <div className="radial-diagram-container">
          {/* Radial Spokes (SVG background) */}
          <svg className="radial-lines-svg" viewBox="0 0 1060 720" preserveAspectRatio="none">
            {/* Center: (530, 360) */}
            {/* 1. Discover */}
            <line x1="530" y1="310" x2="530" y2="190" stroke="#bae6fd" strokeWidth="2" strokeDasharray="5 5" />
            {/* 2. Prepare */}
            <line x1="575" y1="325" x2="745" y2="250" stroke="#bae6fd" strokeWidth="2" strokeDasharray="5 5" />
            {/* 3. Arrive */}
            <line x1="575" y1="395" x2="745" y2="470" stroke="#bae6fd" strokeWidth="2" strokeDasharray="5 5" />
            {/* 4. Verify & Collect */}
            <line x1="530" y1="410" x2="530" y2="530" stroke="#bae6fd" strokeWidth="2" strokeDasharray="5 5" />
            {/* 5. Track */}
            <line x1="485" y1="395" x2="315" y2="470" stroke="#bae6fd" strokeWidth="2" strokeDasharray="5 5" />
            {/* 6. Order */}
            <line x1="485" y1="325" x2="315" y2="250" stroke="#bae6fd" strokeWidth="2" strokeDasharray="5 5" />
          </svg>

          {/* Central Hub Emblem */}
          <div className="radial-center-hub">
            <div className="hub-outer-glow">
              <div className="hub-inner-card">
                <img
                  src={qufree_logo}
                  alt="QuFree Center"
                  className="hub-mascot-img"
                />
                <span className="hub-brand-name">QuFree</span>
              </div>
            </div>
          </div>

          {/* 6 Outer Steps */}
          <div className="radial-steps-grid">
            {radialSteps.map((step) => (
              <div className={`radial-step-item ${step.positionClass}`} key={step.id}>
                <div className="radial-step-icon-wrap">{step.icon}</div>
                <h4 className="radial-step-title">{step.title}</h4>
                <p className="radial-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. "Better for Shoppers. Better for Shops." Section */}
      <section className="qufree-comparison-section">
        <div className="comparison-content">
          <div className="comparison-left-col">
            <h2 className="comparison-title">
              Better for <span className="highlight-teal">Shoppers.</span>
              <br />
              Better for <span className="highlight-yellow">Shops.</span>
            </h2>
          </div>
          <div className="comparison-right-col">
            <p className="comparison-desc">
              For customers, QuFree turns a routine errand into a quick, predictable
              trip — order ahead, walk in, walk out. For vendors, it means fewer
              counter delays, better order visibility, and a simpler way to serve more
              customers without adding staff or changing how the shop runs day to day.
            </p>
          </div>
        </div>
      </section>

      {/* Pre-Footer & Footer */}
      <HomePageFooter page="Marketplace" />
      <Footer />
    </div>
  );
};

export default Qufree;
